"use client";

import { useState } from "react";

type LiteYouTubeProps = {
  /** YouTube video ID, e.g. "TLMMHNrzn8s" */
  videoId: string;
  /** Accessible title for the iframe + play button */
  title: string;
  className?: string;
};

const EMBED_PARAMS = "autoplay=1&rel=0&modestbranding=1";

/**
 * Lightweight YouTube facade.
 *
 * On initial page load this renders only a thumbnail + play button: zero
 * YouTube JavaScript, so the ~460KB base.js + module chunks that normally
 * block first paint never load. The real iframe is injected on the first
 * click/tap (a user gesture, so autoplay is allowed). This is the standard
 * fix for YouTube-destroyed Core Web Vitals.
 *
 * Both the idle (thumbnail) and activated (iframe) states occupy the same
 * aspect-video box, so there is no layout shift on activation.
 */
export default function LiteYouTube({ videoId, title, className = "" }: LiteYouTubeProps) {
  const [activated, setActivated] = useState(false);
  const thumbnail = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

  if (activated) {
    return (
      <div className={`relative aspect-video w-full overflow-hidden rounded-lg ${className}`}>
        <iframe
          className="absolute inset-0 h-full w-full"
          src={`https://www.youtube.com/embed/${videoId}?${EMBED_PARAMS}`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setActivated(true)}
      aria-label={`Play video: ${title}`}
      className={`group relative block aspect-video w-full overflow-hidden rounded-lg bg-black focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/40 ${className}`}
    >
      <img
        src={thumbnail}
        alt=""
        loading="lazy"
        decoding="async"
        width={480}
        height={360}
        className="absolute inset-0 h-full w-full object-cover transition-opacity duration-300 group-hover:opacity-90"
      />
      <span className="absolute inset-0 bg-black/20 transition-colors duration-300 group-hover:bg-black/30" />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary shadow-xl transition-transform duration-200 group-hover:scale-110">
          <svg
            className="ml-1 h-7 w-7 text-primary-content"
            fill="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      </span>
    </button>
  );
}
